import { Component, Input, OnInit, ViewChild, ViewEncapsulation, OnDestroy, ChangeDetectorRef } from '@angular/core';
import * as _ from 'underscore';
import { CorpusViewModel, SmfType } from '../../models/corpus';
import { Endpoint, MessageType, UpdateStatusType, SettingsType, LoadFileType, ConnectorType } from '../../models/enums';
import { ActionType, ObjectActionListType, Permissions } from '../../models/permission';
import { CommonService } from '../../services/common/common.service';
import { RestService } from '../../services/rest/rest.service';
import { OtherLegalDiscoveryOptionsComponent } from '../modals/other-legal-discovery-options-modal/other-legal-discovery-options-modal.component';
import { SettingsModalComponent } from '../modals/settings-modal/settings-modal.component';
import { NameDescriptionComponent } from '../name-description/name-description.component';
import { IndexLevel } from '../../models/index-settings';
import { Subscription } from 'rxjs';
import { CustomDateTimePickerComponent } from '../custom-date-time-picker/custom-date-time-picker.component';

@Component({
    standalone: false,
    selector: 'res-new-data-set-load-file-menu',
    templateUrl: './new-data-set-load-file-menu.component.html',
    styleUrls: ['./new-data-set-load-file-menu.component.scss'],
    encapsulation: ViewEncapsulation.None
})
export class NewDataSetLoadFileMenuComponent implements OnInit, OnDestroy {
    readonly IndexLevel = IndexLevel;
    @ViewChild(NameDescriptionComponent, { static: true }) nameDescriptionComponent: NameDescriptionComponent;
    @ViewChild('dateStart') dateStartComponent: CustomDateTimePickerComponent;
    @ViewChild('dateEnd') dateEndComponent: CustomDateTimePickerComponent;
    readonly SmfType = SmfType;

    @Input() modalAutomationId: string;
    @Input() isLoadFile = false;
    @Input() isSmfDataSetModal = false;

    // name validation info
    validateName = true;

    validateNameRequest = {
        endpoint: Endpoint.viewManager_validateName,
        request: (): Record<string, unknown> => {
            return {
                name: this.nameDescriptionComponent.name,
                projectHandle: this.commonService.currentProject.handle,
                objectType: this.nameDescriptionComponent.drObject.objectType
            };
        }
    };

    nameDescriptionAutoId = 'new-data-set-load-file';

    // other fields
    indexLevelType = IndexLevel.ANALYTIC;
    fileProfiles = [];
    isPatternDetectionEnabled = true;
    addToProjectData = false;
    indexSettingsInput = 'Current Project Settings';
    patternDetectionInput = 'Current Project Settings';
    selectedLoadFileProfileId = '';
    batchName = '';
    setAttributes: any;
    private entityChangedEvent: Subscription;
    private connectorChangedEvent: Subscription;
    private pathChangedEvent: Subscription;
    defaultDatePlaceholders = ['On or after (yyyy-MM-dd ...', 'On or before yyyy-MM-dd ...'];
    dateObject = {
        startDate: null, endDate: null, searchTerms: null
    }
    dateError: boolean;
    is365Connector: boolean;
    isGoogleWorkspace: boolean;
    isTemplateSelectDisabled = true;
    selectedFileType: LoadFileType = LoadFileType.CONCORDANCE_DAT;

    // permissions
    hasIdxSettingsPermissions = true;
    hasPatternsSettingsPermissions = true;
    hasAddToProjectPermissions = true;
    hasLoadFilePermissions = true;

    smfType = SmfType.Cellebrite;

    constructor(private commonService: CommonService, private restService: RestService, public changeDetector: ChangeDetectorRef,) {
        this.entityChangedEvent = this.commonService.getMessage(MessageType.entityChanged).subscribe((data) => {
            if (data && data.updateStatusType === UpdateStatusType.TEMPLATE_MEMEBERSHIP) {
                this.loadFileProfile();
            }
        });
        this.connectorChangedEvent = this.commonService.getMessage(MessageType.connectorChanged).subscribe((data) => {
            if (data && data.connector) {
                const rawPaths = data?.paths;
                const paths = Array.isArray(rawPaths) ? rawPaths : rawPaths ? [rawPaths] : [];
                const containsUsersPath = paths.some(path => path?.toLowerCase().includes('/users'));
                this.is365Connector = data.connector?.type === 'Exchange' && containsUsersPath ? true : false;
                this.isGoogleWorkspace = data.connector?.type === ConnectorType.GoogleWorkspace ? true : false;
            }
        });
        this.pathChangedEvent = this.commonService.getMessage(MessageType.loadFilePathChanged).subscribe((data) => {
            if (data?.filePath !== null) {
                this.isTemplateSelectDisabled = false;
                if (data.filePath.endsWith('.xml')) {
                    this.selectedFileType = LoadFileType.EDRM_XML;
                } else {
                    this.selectedFileType = LoadFileType.CONCORDANCE_DAT;
                }
                this.loadFileProfile();
            } else {
                this.isTemplateSelectDisabled = true;
                this.selectedLoadFileProfileId = '';
                this.selectedFileType = null;
            }
        });
    }

    ngOnInit(): void {
        if (this.isLoadFile) {
            this.hasLoadFilePermissions = Permissions.checkUserPermissions([{ permissionType: ObjectActionListType.PRJ_LOADFILE_SETTINGS, permissionActions: [ActionType.CREATE] }]);
        }
        this.nameDescriptionComponent.drObject = new CorpusViewModel();
        this.hasIdxSettingsPermissions = Permissions.checkUserPermissions([{
            permissionType: ObjectActionListType.PRJ_INDEX_SETTINGS,
            permissionActions: [ActionType.CREATE]
        }]);
        this.hasPatternsSettingsPermissions = Permissions.checkUserPermissions([{
            permissionType: ObjectActionListType.PRJ_REGULAR_EXPRESSIONS,
            permissionActions: [ActionType.CREATE]
        }]);
        this.hasAddToProjectPermissions = Permissions.checkUserPermissions([{ permissionType: ObjectActionListType.ECA_CASE_DATA, permissionActions: [ActionType.CREATE] }]);
    }

    setIndexLevelType(indexLevelType: IndexLevel): void {
        this.indexLevelType = indexLevelType;
    }

    setSmfType(smfType: SmfType): void {
        this.smfType = smfType;
    }

    openSettings(setting: any): void {
        this.commonService.openModal(SettingsModalComponent, { modalData: { setting: setting, openType: SettingsType.project }, class: 'vertical-percent-100 horizontal-percent-100', backdrop: false, keyboard: false });
    }

    loadFileProfile(): void {
        this.fileProfiles = [];
        const currentProject = this.commonService.currentProject.handle;
        this.restService
            .post(Endpoint.projectManager_listLoadFileMetadataProfiles, { handle: currentProject })
            .then(response => {
                response.profiles.forEach(profile => {
                    if (!profile.loadFileType || (profile.loadFileType === this.selectedFileType)) {
                        this.fileProfiles.push({
                            name: profile.name,
                            id: profile.id,
                            defaultProfileText: profile.defaultProfile ? '(default)' : ''
                        });
                    }
                });
            });
    }

    getIndexLevelSetting(): any {
        const typeLists = [{
            indexLevelType: IndexLevel.DO_NOT_INDEX,
            indexLevelSetting: ['NO_INDEX'],
            name: 'Do Not Index'
        }, {
            indexLevelType: IndexLevel.SYSTEM_METADATA,
            indexLevelSetting: ['SYSTEM_METADATA_INDEX'],
            name: 'System Metadata'
        }, {
            indexLevelType: IndexLevel.FILE_METADATA,
            indexLevelSetting: ['METADATA_INDEX'],
            name: 'File Metadata'
        }, {
            indexLevelType: IndexLevel.CONTENT,
            indexLevelSetting: ['CONTENT_INDEX'],
            name: 'Content Index'
        }, {
            indexLevelType: IndexLevel.ANALYTIC,
            indexLevelSetting: ['CONTENT_INDEX', 'VECTOR_SET'],
            name: 'Analytic Index'
        }];

        const typeList = _.findWhere(typeLists, { indexLevelType: this.indexLevelType });
        return typeList;
    }

    otherLegalDiscoveryOptions(): void {
        this.commonService.openModal(OtherLegalDiscoveryOptionsComponent, { modalData: this.setAttributes }).then((modalResponse) => {
            if (modalResponse && modalResponse.modalResult) {
                this.setAttributes = modalResponse.modalData;
            }
        });
    }

    onBatchNameFocusLost() {
        this.batchName = this.batchName.trim();
    }

    onDateClick(datePicker: any, mode: any): void {
        if (datePicker && datePicker.target) {
            const crtControl = datePicker.target;
            const defaultPlaceholder = mode === 'start' ? this.defaultDatePlaceholders[0] : this.defaultDatePlaceholders[1];
            if (crtControl.placeholder) {
                if (this.defaultDatePlaceholders.indexOf(crtControl.placeholder) !== -1) {
                    crtControl.placeholder = 'yyyy-MM-dd';
                } else {
                    crtControl.placeholder = defaultPlaceholder;
                }
            } else if (crtControl.id === 'pickerClearBtn') {   // x button was clicked
                crtControl.parentElement.children['datepickerinput'].placeholder = defaultPlaceholder;
            } else if (crtControl.parentElement && crtControl.parentElement.id === 'pickerClearBtn') {  // x button icon was clicked
                crtControl.parentElement.parentElement.children['datepickerinput'].placeholder = defaultPlaceholder;
            }
        }
    }

    // on date picker input lost focus and date was not changed reset placeholder to default
    onFocusLost(datePicker: any, mode: any): void {
        if (datePicker && datePicker.target) {
            const crtControl = datePicker.target;
            const defaultPlaceholder = mode === 'start' ? this.defaultDatePlaceholders[0] : this.defaultDatePlaceholders[1];
            if (crtControl.placeholder) {
                if (this.defaultDatePlaceholders.indexOf(crtControl.placeholder) === -1) {
                    crtControl.placeholder = defaultPlaceholder;
                }
            }
            this.changeDetector.detectChanges();
            setTimeout(() => {
                this.compareDateRange();
            }, 500);
        }
    }

    dateChanged(type?: any, value?: any): void {
        this.dateStartComponent?.errorPopover?.hide();
        this.dateEndComponent?.errorPopover?.hide();

        if (this.dateObject?.startDate?.toString() === 'Invalid Date' || this.dateObject?.endDate?.toString() === 'Invalid Date') {
            this.dateError = true;
        } else {
            this.dateError = false;
        }

        this.compareDateRange();
    }

    compareDateRange(): void {
        this.dateError = null;
        if ((this.dateObject.startDate && this.dateObject.endDate) && this.dateObject.endDate.getTime() < this.dateObject.startDate.getTime()) {
            this.dateError = true;
        } else {
            this.dateError = false;
        }
        this.changeDetector.detectChanges();
    }

    ngOnDestroy(): void {
        if (this.entityChangedEvent) {
            this.entityChangedEvent.unsubscribe();
        }
        if (this.connectorChangedEvent) {
            this.connectorChangedEvent.unsubscribe();
        }
        if (this.pathChangedEvent) {
            this.pathChangedEvent.unsubscribe();
        }
    }
}
